import { Check } from '@sparkware/app-utils';
import {ID_VERIFICATION_STATUS} from '../../../../oauth/oauth_const';
import {TimeSpan} from "@sparkware/app-utils";
import OptionsComponentController from '../../../../../components/options/options_component_controller';
import COMBaseRegulation from "../com_base_regulation";
import { TSR_TYPE } from '../../../../../../../common/interfaces/orbit-component-to-sdk-adapter/components-props/time-spent-reminder-options';

interface AccountData {
    IdVerificationStatus: number,
    AddressVerificationStatus: number,
    RealityCheckDataIntervalInMins: number
}

export default class UKRegulation extends COMBaseRegulation {
    static CLASS = 'UKRegulation';

    private _getAccountData(): AccountData {
        return this.serviceFactory.userService().getAccountData() as AccountData;
    }

    isAnonymousPlayAllowed() : boolean {

        if(!this.serviceFactory.oAuth2Service().isAuthorized()) {
            return false;
        }

        return this._getAccountData().IdVerificationStatus === ID_VERIFICATION_STATUS.AGE_VERIFIED;
    }

    forceDemoButton(): boolean {

        return this.isAnonymousPlayAllowed() && this._getAccountData().AddressVerificationStatus !== ID_VERIFICATION_STATUS.ADDRESS_VERIFIDED;
    }

    protected realityCheckEnabled(): boolean {
        return true;
    }

    getRealityCheckInterval(): TimeSpan {
        if (this.serviceFactory.skinService().useControlCenterForReminderInterval()) {
            if (this._getAccountData() && !Check.isNullOrUndefined(this._getAccountData().RealityCheckDataIntervalInMins)) {
                return TimeSpan.fromMinutes(this._getAccountData().RealityCheckDataIntervalInMins);
            }
        }
        return this.serviceFactory.componentFactoryService().createComponent(OptionsComponentController).getReminderInterval();
    }

    async updateRealityCheckInterval(realityCheckIntevalMin: number): Promise<void> {
        if (!this.serviceFactory.oAuth2Service().isAuthorized()) {
            return Promise.reject();
        }

        // Update reminders
        // Set the reminder interval in account details and control center
        if (this.serviceFactory.skinService().useControlCenterForReminderInterval()) {
            let updateRealityCheckDataUrl = this.serviceFactory.userService().getUpdateRealityCheckDataUrl();
            try {
                // updates reality check in Control Center. Use this endpoint to migrate existing reality check
                await this.serviceFactory.http2Service().post(updateRealityCheckDataUrl, { realityCheckIntevalMin });
            } catch (error) {
                this.logger.error("Could not call update reality check to update the interval. url: " + updateRealityCheckDataUrl, error);
            }
            // when the call is done, update the account details for the current session
            const accountData = this.serviceFactory.userService().getAccountData();
            if (accountData && !Check.isNullOrUndefined(accountData.RealityCheckDataIntervalInMins)) {
                accountData.RealityCheckDataIntervalInMins = realityCheckIntevalMin;
                this.serviceFactory.userService().updateTokenData("accountData", accountData);
            }
        } else {
            // Set the reminder that use the setting from localStorage
            OptionsComponentController.create().load('ReminderSettings').set(realityCheckIntevalMin);
        }
        return Promise.resolve();
    }

    async attemptRealityCheckIntervalMigrationFromLocalStorageToControlCenter(): Promise<void> {
        if (!this.serviceFactory.oAuth2Service().isAuthorized()) {
            return Promise.reject();
        }
        if (this.serviceFactory.skinService().useControlCenterForReminderInterval()) {
            // when using Control Center for reminder interval, first we check if the data was migrated from Storage to Control Center.
            let controlCenterReminderInterval: TimeSpan | undefined = undefined;
            if (this._getAccountData() && !Check.isNullOrUndefined(this._getAccountData().RealityCheckDataIntervalInMins)) {
                controlCenterReminderInterval = TimeSpan.fromMinutes(this._getAccountData().RealityCheckDataIntervalInMins);
            }

            if (controlCenterReminderInterval && controlCenterReminderInterval.totalMinutes > 0) {
                // 1. If data exists in Control center, ignore if anything exists in Local Storage. Attempt to delete from Local Storage
                OptionsComponentController.create().load('ReminderSettings').off();
                return Promise.resolve();
            }

            const localStorageReminderInterval: TimeSpan = this.serviceFactory.componentFactoryService().createComponent(OptionsComponentController).getReminderInterval();
            if (localStorageReminderInterval.totalMinutes > 0) {
                // 2. If data does not exists in Control Center, but exists in Local Storage, migrate:
                let updateRealityCheckDataUrl = this.serviceFactory.userService().getUpdateRealityCheckDataUrl();
                try {
                    await this.serviceFactory.http2Service().post(updateRealityCheckDataUrl, { realityCheckIntevalMin: localStorageReminderInterval.totalMinutes });
                    // and remove data from Local Storage with:
                    OptionsComponentController.create().load('ReminderSettings').off();

                    // when the call is done, update the account details for the current session
                    const accountData = this.serviceFactory.userService().getAccountData();
                    if (accountData && !Check.isNullOrUndefined(accountData.RealityCheckDataIntervalInMins)) {
                        accountData.RealityCheckDataIntervalInMins = localStorageReminderInterval.totalMinutes;
                        this.serviceFactory.userService().updateTokenData("accountData", accountData);
                    }
                } catch (error) {
                    this.logger.error("Could not call update reality check to update the interval. url: " + updateRealityCheckDataUrl, error);
                    return Promise.reject();
                }
            }
        }
        return Promise.resolve();
    }

    getRealityCheckIntervalExpectedByTheGame() {
        let interval = this.getRealityCheckInterval();
        return {
            timeSpan: interval,
            expectedValueByTheGame: Math.round(interval.totalSeconds)
        }
    }

    shouldHaveBetLimitsPerBrand(): boolean {
        return true;
    }

    showNetPosition(): boolean {
        return true;
    }

    sessionTimerEnabled() {
        const isMiniCip = this.serviceFactory.integrationService().isMiniCip();
        return !isMiniCip && (this.getRealityCheckInterval().totalMilliseconds > 0);
    }

    _handleRealityCheckMessage(sessionTimer) {
        this.serviceFactory.gameService().stopCurrentGameAutoplay();

        this.serviceFactory.orbitComponentRenderService().showTimeSpentReminderWithDropdown({
            timerType: TSR_TYPE.SESSION_TIMER,
            playingTime: this.getCurrentSessionTime()
        });
    }

}